import React from 'react';
import { Icon } from '../Icon/Icon.js';
import './DropdownMenu.css';

/**
 * DropdownMenu — a unified, composable dropdown menu.
 *
 * Usage:
 *   <DropdownMenu align="right">
 *     <DropdownMenu.Item onClick={…}>Rename</DropdownMenu.Item>
 *     <DropdownMenu.Item selected>Default</DropdownMenu.Item>
 *     <DropdownMenu.Divider />
 *     <DropdownMenu.Item danger onClick={…}>Delete</DropdownMenu.Item>
 *   </DropdownMenu>
 */

/* ---------- Menu wrapper ---------- */

export const DropdownMenu = ({
  children,
  align = 'right',   // 'right' | 'left'
  fixed = false,      // true  → position: fixed (for portals)
  className = '',
  style = {},
  menuRef,
  ...props
}) => {
  const menuClasses = [
    'dropdown-menu',
    `dropdown-menu--align-${align}`,
    fixed && 'dropdown-menu--fixed',
    className
  ].filter(Boolean).join(' ');

  return (
    <div ref={menuRef} className={menuClasses} style={style} role="menu" {...props}>
      {children}
    </div>
  );
};

/* ---------- Item ---------- */

const DropdownMenuItem = ({
  children,
  onClick,
  selected = false,
  danger = false,
  className = '',
  ...props
}) => {
  const itemClasses = [
    'dropdown-menu__item',
    selected && 'dropdown-menu__item--selected',
    danger && 'dropdown-menu__item--danger',
    className
  ].filter(Boolean).join(' ');

  return (
    <button className={itemClasses} onClick={onClick} role="menuitem" {...props}>
      <span className="dropdown-menu__item-label">{children}</span>
      {selected && (
        <Icon name="check" size="small" className="dropdown-menu__item-check" />
      )}
    </button>
  );
};

/* ---------- Divider ---------- */

const DropdownMenuDivider = () => (
  <div className="dropdown-menu__divider" role="separator" />
);

/* ---------- Attach sub-components ---------- */

DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.Divider = DropdownMenuDivider;
